import { LoadingOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { AiPrefillButton } from 'features/libra/stepComponents/AiPrefillButton'
import {
  AiPrefillGate,
  prefillLabel,
} from 'features/libra/stepComponents/AiPrefillControls'
import { useGongAgentAutofill } from 'features/libra/stepComponents/GongAgentAutofill'
import { useZoneAnalysisData } from 'features/libra/stepComponents/zoneAnalysis/ZoneAnalysisDataProvider'
import {
  getZoneTabAgentPermissions,
  parseBreakdownPrefill,
  parseCofPrefill,
  ZONE_TAB_SECTION_IDS,
} from 'features/libra/stepComponents/zoneAnalysis/zoneTabAutofill'
import { FC, useCallback, useMemo, useState } from 'react'
import { RolodexPermissionsValues } from 'types'

const AiHeaderActionButton: FC<{
  permissions: RolodexPermissionsValues[]
  tooltip: string
  loading: boolean
  applied: boolean
  disabled?: boolean
  onClick: () => void
}> = ({ permissions, tooltip, loading, applied, disabled, onClick }) => (
  <AiPrefillGate permissions={permissions}>
    <AiPrefillButton
      type='primary'
      size='small'
      icon={loading ? <LoadingOutlined spin /> : <ThunderboltOutlined />}
      onClick={onClick}
      disabled={disabled || loading}
      tooltip={tooltip}>
      {prefillLabel({ loading, hasRun: applied })}
    </AiPrefillButton>
  </AiPrefillGate>
)

export interface CofAiPrefillButtonProps {
  sectionId: string
  permissions: RolodexPermissionsValues[]
  onApply: (cof: {
    level: 'low' | 'medium' | 'high'
    justification?: string
  }) => void
  readonly?: boolean
}

/**
 * Pre-fills ticket-level Consequence of Failure from the highest numeric SHE
 * CoF across the unit's APM components; missing numeric CoF is not written.
 */
export const CofAiPrefillButton: FC<CofAiPrefillButtonProps> = ({
  sectionId,
  permissions,
  onApply,
  readonly,
}) => {
  const [outcome, setOutcome] = useState<'applied' | 'unfound' | null>(null)

  const onFieldValues = useCallback(
    (fieldValues: FieldValues) => {
      const cof = parseCofPrefill(fieldValues)
      if (!cof) {
        setOutcome('unfound')
        return
      }
      setOutcome('applied')
      onApply(cof)
    },
    [onApply]
  )

  const { autofill, loading, error } = useGongAgentAutofill({
    sectionId,
    onFieldValues,
  })

  const description = useMemo(() => {
    if (error) return error
    if (outcome === 'unfound')
      return 'No numeric SHE CoF found in the APM component data — set the level manually.'
    if (outcome === 'applied')
      return 'CoF level set from the highest SHE CoF across the asset’s APM components.'
    return undefined
  }, [error, outcome])

  if (readonly) return null

  return (
    <AiHeaderActionButton
      permissions={permissions}
      tooltip={
        description ?? 'Pre-fill Consequence of Failure with AI from APM data.'
      }
      loading={loading}
      applied={outcome === 'applied'}
      onClick={autofill}
    />
  )
}

export interface ZoneBreakdownAiPrefillButtonProps {
  onApply: (justification: string) => void
  readonly?: boolean
}

/**
 * Pre-fills the ticket-level Zone Breakdown Justification from confirmed zones
 * without proposing a different breakdown.
 */
export const ZoneBreakdownAiPrefillButton: FC<
  ZoneBreakdownAiPrefillButtonProps
> = ({ onApply, readonly }) => {
  const { config, zones } = useZoneAnalysisData()
  const [outcome, setOutcome] = useState<'applied' | 'unfound' | null>(null)

  const confirmedZones = useMemo(
    () => zones.filter((z) => !z.ai_suggested),
    [zones]
  )
  const inputs = useMemo(
    () => ({
      zones: confirmedZones.map((z) => ({
        title: z.title,
        description: z.description,
      })),
    }),
    [confirmedZones]
  )

  const onFieldValues = useCallback(
    (fieldValues: FieldValues) => {
      const justification = parseBreakdownPrefill(fieldValues)
      if (!justification) {
        setOutcome('unfound')
        return
      }
      setOutcome('applied')
      onApply(justification)
    },
    [onApply]
  )

  const { autofill, loading, error } = useGongAgentAutofill({
    sectionId: ZONE_TAB_SECTION_IDS.zone_breakdown,
    onFieldValues,
    inputs,
  })

  const noZones = confirmedZones.length === 0

  const description = useMemo(() => {
    if (error) return error
    if (outcome === 'unfound')
      return 'The AI could not produce a justification — write it manually.'
    if (outcome === 'applied')
      return `Justification written from the ${confirmedZones.length} confirmed zone(s).`
    if (noZones) return 'Confirm at least one zone first.'
    return undefined
  }, [error, outcome, noZones, confirmedZones.length])

  if (readonly) return null

  return (
    <AiHeaderActionButton
      permissions={
        getZoneTabAgentPermissions(
          config,
          'zone_breakdown'
        ) as RolodexPermissionsValues[]
      }
      tooltip={
        noZones
          ? 'Confirm at least one zone first.'
          : (description ??
            'Pre-fill this justification with AI from the confirmed zones.')
      }
      loading={loading}
      applied={outcome === 'applied'}
      disabled={noZones}
      onClick={autofill}
    />
  )
}
